/**
 * @auth 赖传峰
 * @date 2022/4/2
 */
import { type CaptchaRequestOptions } from '@xbase/captcha'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { getLocale } from '~/plugins/i18n'

/* eslint-disable-next-line */
interface UseFetchReturn<T = any, P = any> {
  /**
   * 请求返回的数据
   */

  data: Ref<T>;

  /**
   * 请求出现的错误
   */

  error: Ref<P>;
}

/* eslint-disable-next-line */
export type UseFetch = <T = any, P = GeneralError | any>(
  params: string,
  options?: CaptchaRequestOptions,
) => UseFetchReturn & PromiseLike<UseFetchReturn<T, P>>;

const isGeneralError = (err: unknown): err is GeneralError => !!(
  err
  && typeof err === 'object'
  && !Array.isArray(err)
  && (err as GeneralError).error_description
)

/**
 * 支持以下两种调用方式
 * const { data, error } = useFetch();
 * const { data, error } = await useFetch();
 *
 * @param url { string } 请求地址
 * @param options { CaptchaRequestOptions } 请求参数
 */
const useXFetch: UseFetch = (url: string, options: CaptchaRequestOptions = {}) => {
  const responseData: UseFetchReturn = {
    data: ref(null),
    error: ref(null),
  }

  const headers = { 'Accept-Language': getLocale(), ...options.headers }

  const task = async () => {
    try {
      // NOTICE 需要用懒加载，否则 navigator 会阻塞编译
      responseData.data.value = await (await (await import('~/utils/xbase')).xBase)?.$http(
        url,
        { ...options, headers },
      )
    } catch (err) {
      console.error('fetch api error: ', err)
      responseData.error.value = isGeneralError(err)
        ? { ...err, message: err.error_description }
        : err
    }
    return responseData
  }

  const asyncHandler = task()
  return {
    ...responseData,
    then(onfulfilled) {
      return asyncHandler.then(onfulfilled)
    },
  }
}

export default useXFetch
